/*
  KinkKart cart drawer (cart-v2, 2026-10-10). Replaces Dawn 15.4.1's file.

  Changes from Dawn, and why:
  - One render path. Every add and cartUpdate ends in renderDrawer(html), which
    swaps the whole .drawer__inner and keeps the host's is-empty flag in step.
    Dawn's cartUpdate handler swapped single pieces and only when both copies
    existed, so the first add to an empty cart, and the last removal, left the
    drawer showing the wrong state until a reload.
  - Adds that already asked for the drawer section (product-form, the picks,
    quick add) render from that HTML, with no second request. Their publishers
    call renderContents themselves, so the cartUpdate handler skips them.
  - The line that was just added gets a "Just added" flag and scrolls into view.
  - Focus is trapped once the panel is open, and re-trapped after a re-render
    (Dawn could trap focus inside a drawer that was already closing).
  - Phone scroll lock that holds on iOS: position: fixed + the saved scrollY,
    counted, so the quick-add sheet can hand the page straight to the drawer.
  - On /cart the header icon goes to the page instead of opening the drawer.
  - Back from checkout (bfcache) re-syncs the drawer and the count bubble.
  - <kk-cart-upsell>: the picks inside the drawer.
  - <kk-quick-add>: the same size picker on every product card (hover panel on
    desktop, bottom sheet on touch). Picking a colour there switches the card's
    photo to that colour and points the card's link at it.
  - <kk-sticky-atc>: the product page's bottom bar, driving the page's own
    picker and Add to cart button.

  Dawn's cart.js (CartItems) still runs quantity changes and removals.
  Markup: snippets/cart-drawer.liquid, kk-cart-upsell.liquid, kk-quick-add.liquid,
  sections/kk-sticky-atc.liquid.
*/

const KK_SLIDE_MS = 340;

function kkAddedKeys(state) {
  if (!state) return [];
  if (Array.isArray(state.items)) return state.items.map((item) => item.key).filter(Boolean);
  return state.key ? [state.key] : [];
}

function kkAddedLabel(state) {
  const item = state && (Array.isArray(state.items) ? state.items[0] : state);
  if (!item || !item.product_title) return '';
  const variant = item.variant_title ? ` (${item.variant_title})` : '';
  return `${item.product_title}${variant}`;
}

// fixed: always two decimals, like Shopify's own "$36.00"; otherwise "+$3".
function kkMoney(cents, fixed = false) {
  try {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
      minimumFractionDigits: fixed || cents % 100 ? 2 : 0,
    }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
}

function kkReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Captured before anything can stand in for it (see holdScrollForBarChoices).
const kkScrollTo = window.scrollTo.bind(window);

// Instant, whatever the page's CSS scroll-behavior says.
function kkJumpTo(y) {
  const root = document.documentElement;
  const behavior = root.style.scrollBehavior;
  root.style.scrollBehavior = 'auto';
  kkScrollTo(0, y);
  root.style.scrollBehavior = behavior;
}

/* ---------- Page scroll lock, shared by the drawer and the quick-add sheet ---------- */

let kkLockCount = 0;
let kkLockedY = 0;

function kkLock() {
  kkLockCount += 1;
  if (kkLockCount > 1) return;
  document.body.classList.add('overflow-hidden');
  // iOS Safari scrolls the page behind an overflow:hidden body; pinning it doesn't.
  if (!window.matchMedia('(max-width: 749px), (pointer: coarse)').matches) return;
  kkLockedY = window.scrollY || document.documentElement.scrollTop || 0;
  document.body.style.top = `-${kkLockedY}px`;
  document.body.classList.add('kk-cart-lock');
}

function kkUnlock() {
  if (kkLockCount === 0) return;
  kkLockCount -= 1;
  if (kkLockCount > 0) return;
  document.body.classList.remove('overflow-hidden');
  if (!document.body.classList.contains('kk-cart-lock')) return;
  document.body.classList.remove('kk-cart-lock');
  document.body.style.top = '';
  kkJumpTo(kkLockedY);
}

/* ---------- Size picker, shared by the cart picks and quick add ---------- */

// The variant table from snippets/kk-variant-data.liquid, plus the colour in play.
function kkReadVariants(root) {
  let data;
  try {
    data = JSON.parse(root.querySelector('[data-kk-variants]').textContent);
  } catch (e) {
    return null;
  }
  const pressed = root.querySelector('[data-kk-color][aria-pressed="true"]');
  if (pressed) {
    data.color = pressed.dataset.kkColor;
  } else if (data.colorIndex >= 0) {
    const first = data.variants.find((v) => v.available) || data.variants[0];
    data.color = first ? first.options[data.colorIndex] : null;
  }
  return data;
}

function kkVariantFor(data, size) {
  return data.variants.find(
    (v) =>
      (data.colorIndex < 0 || v.options[data.colorIndex] === data.color) &&
      (data.sizeIndex < 0 || !size || v.options[data.sizeIndex] === size)
  );
}

function kkShownVariant(data) {
  const inColour = data.variants.filter((v) => data.colorIndex < 0 || v.options[data.colorIndex] === data.color);
  return inColour.find((v) => v.available) || inColour[0];
}

// Swatches, colour name, size availability, "+$3" on dearer sizes, the cart's size.
// mode 'add': a size button adds (picks, quick add); 'select': it only picks (sticky bar).
function kkPaint(root, data, yourSize, mode = 'add') {
  const inColour = data.variants.filter((v) => data.colorIndex < 0 || v.options[data.colorIndex] === data.color);
  if (!inColour.length) return;
  const base = Math.min(...inColour.map((v) => v.price));

  root.querySelectorAll('[data-kk-color]').forEach((swatch) => {
    const colour = swatch.dataset.kkColor;
    swatch.setAttribute('aria-pressed', colour === data.color ? 'true' : 'false');
    swatch.disabled = !data.variants.some((v) => v.options[data.colorIndex] === colour && v.available);
  });
  const colourName = root.querySelector('[data-kk-colour-name]');
  if (colourName && data.color) colourName.textContent = data.color;

  root.querySelectorAll('[data-kk-size]').forEach((button) => {
    const size = button.dataset.kkSize;
    const variant = kkVariantFor(data, size);
    const ok = !!(variant && variant.available);
    const mine = !!yourSize && size === yourSize;
    button.disabled = !ok;
    button.classList.toggle('is-yours', mine);

    let extra = button.querySelector('.kk-size-extra');
    const diff = variant ? variant.price - base : 0;
    if (diff > 0) {
      if (!extra) {
        extra = document.createElement('span');
        extra.className = 'kk-size-extra';
        button.appendChild(extra);
      }
      extra.textContent = `+${kkMoney(diff)}`;
    } else if (extra) {
      extra.remove();
    }

    const parts = mode === 'select' ? [`Size ${size}`] : [`Add ${data.title}`];
    if (mode !== 'select' && data.color) parts.push(data.color);
    if (mode !== 'select' && size) parts.push(`size ${size}`);
    if (diff > 0) parts.push(`${kkMoney(diff)} extra`);
    if (mine) parts.push('the size already in your cart');
    if (!ok) parts.push('sold out');
    button.setAttribute('aria-label', parts.join(', '));
  });

  const shown = kkShownVariant(data);
  root.querySelectorAll('[data-kk-img]').forEach((img) => {
    if (shown && shown.img && img.getAttribute('src') !== shown.img) img.src = shown.img;
  });
}

// One add for the picks and quick add: asks for the drawer + bubble sections, renders
// them, then tells the rest of the page. Rejects with the store's message on failure.
function kkAddVariant(variantId, source) {
  const drawer = document.querySelector('cart-drawer');
  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  config.body = JSON.stringify({
    items: [{ id: variantId, quantity: 1 }],
    sections: drawer ? drawer.getSectionsToRender().map((section) => section.id) : [],
    sections_url: window.location.pathname,
  });

  return fetch(`${routes.cart_add_url}`, config)
    .then((response) => response.json())
    .then((state) => {
      if (state.status) throw new Error(state.description || state.message || window.cartStrings.error);
      if (drawer) {
        drawer.renderContents(state);
      } else {
        window.location = window.routes.cart_url;
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source, productVariantId: variantId, cartData: state });
      return state;
    });
}

/* ---------- The drawer ---------- */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // On the document, not the drawer: after a re-render removes the focused
    // button, focus sits on <body> and a drawer-level listener never hears Esc.
    document.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape' && this.classList.contains('active')) this.close();
    });
    // Delegated, so it survives every re-render of the panel.
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay') {
        this.close();
        return;
      }
      const jump = evt.target.closest('[data-kk-jump]');
      if (jump) {
        const picks = this.querySelector('#kk-upsell');
        if (!picks) return;
        evt.preventDefault();
        picks.scrollIntoView({ behavior: kkReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      }
    });
    this.setHeaderCartIconAccessibility();

    window.addEventListener('pageshow', (evt) => {
      if (evt.persisted) this.refresh();
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    // On the cart page the page is the cart; a drawer over it would be a second copy.
    if (document.getElementById('main-cart-items')) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.lockScroll();

    if (this.classList.contains('active')) {
      this.trap();
      return;
    }

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });
    clearTimeout(this.trapTimer);
    this.trapTimer = setTimeout(() => this.trap(), KK_SLIDE_MS);
  }

  close() {
    clearTimeout(this.trapTimer);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockScroll();
  }

  trap() {
    if (!this.classList.contains('active')) return;
    const panel = this.querySelector('.drawer__inner');
    if (panel) trapFocus(document.getElementById('CartDrawer'), panel);
  }

  lockScroll() {
    if (this.holdsLock) return;
    this.holdsLock = true;
    kkLock();
  }

  unlockScroll() {
    if (!this.holdsLock) return;
    this.holdsLock = false;
    kkUnlock();
  }

  // Called by product-form.js and kkAddVariant() with the /cart/add response.
  renderContents(parsedState) {
    this.productId = parsedState.id;
    const sections = parsedState.sections || {};
    const added = kkAddedKeys(parsedState);

    if (sections['cart-icon-bubble']) this.renderBubble(sections['cart-icon-bubble']);
    if (sections['cart-drawer']) {
      this.renderDrawer(sections['cart-drawer'], added);
    } else {
      this.refresh({ added });
    }

    const label = kkAddedLabel(parsedState);
    if (label) this.announce(`Added ${label} to your cart.`);

    setTimeout(() => this.open());
  }

  renderDrawer(html, added = []) {
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
    const target = this.querySelector('.drawer__inner');
    if (!source || !target) return;

    const oldBody = this.querySelector('cart-drawer-items');
    const scrollTop = oldBody ? oldBody.scrollTop : 0;

    target.innerHTML = source.innerHTML;
    const body = this.querySelector('cart-drawer-items');
    this.classList.toggle('is-empty', !body);
    if (body) body.scrollTop = scrollTop;

    this.flagAdded(added);
    if (this.classList.contains('active')) this.trap();
  }

  renderBubble(html) {
    const bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (source) bubble.innerHTML = source.innerHTML;
  }

  refresh({ open = false, added = [] } = {}) {
    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => {
        if (sections['cart-drawer']) this.renderDrawer(sections['cart-drawer'], added);
        if (sections['cart-icon-bubble']) this.renderBubble(sections['cart-icon-bubble']);
        if (open) this.open();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  flagAdded(keys) {
    if (!keys || !keys.length) return;
    let first = null;
    keys.forEach((key) => {
      const line = this.querySelector(`.kk-line[data-line-key="${CSS.escape(key)}"]`);
      if (!line) return;
      line.classList.add('is-new');
      if (!first) first = line;
    });
    const body = this.querySelector('cart-drawer-items');
    if (!first || !body) return;
    const behavior = kkReducedMotion() ? 'auto' : 'smooth';
    // A new line lands first: show it with the pair meter above it.
    if (first === this.querySelector('.kk-line')) {
      body.scrollTo({ top: 0, behavior });
    } else {
      first.scrollIntoView({ block: 'nearest', behavior });
    }
  }

  // Lives outside .drawer__inner, so re-renders don't reset it mid-announcement.
  announce(message) {
    const region = this.querySelector('#KkCartAnnounce');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => {
      region.textContent = message;
    }, 60);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  // Dawn's CartItems calls onCartUpdate() without the event; the drawer needs it
  // to reuse section HTML that came with the change instead of fetching again.
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
  }

  onCartUpdate(event = {}) {
    const drawer = this.closest('cart-drawer');
    if (!drawer) return;

    // cart.js just re-rendered the panel for a quantity change or removal; put
    // the list back where it was rather than at the top.
    if (event.source === 'cart-items') {
      if (typeof drawer.kkScrollTop === 'number') {
        this.scrollTop = drawer.kkScrollTop;
        drawer.kkScrollTop = null;
      }
      return;
    }
    // These call renderContents themselves right after publishing.
    if (['product-form', 'kk-cart-upsell', 'kk-quick-add'].includes(event.source)) return;

    const sections = event.cartData && event.cartData.sections;
    if (sections && sections['cart-drawer']) {
      drawer.renderDrawer(sections['cart-drawer']);
      if (sections['cart-icon-bubble']) drawer.renderBubble(sections['cart-icon-bubble']);
      return;
    }
    return drawer.refresh();
  }

  // Only the quantity inputs drive cart.js; nothing else in the drawer should.
  onChange(event) {
    if (!event.target.matches || !event.target.matches('.quantity__input')) return;
    super.onChange(event);
  }

  enableLoading(line) {
    const drawer = this.closest('cart-drawer');
    if (drawer) drawer.kkScrollTop = this.scrollTop;
    super.enableLoading(line);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

/* ---------- The picks inside the drawer ---------- */

class KkCartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.querySelectorAll('[data-kk-card]').forEach((card) => {
      card.kk = kkReadVariants(card);
      if (!card.kk) {
        card.hidden = true;
        return;
      }
      kkPaint(card, card.kk, card.dataset.yourSize);
    });
    this.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    const card = event.target.closest('[data-kk-card]');
    if (!card || !card.kk) return;

    const swatch = event.target.closest('[data-kk-color]');
    if (swatch) {
      if (swatch.disabled) return;
      card.kk.color = swatch.dataset.kkColor;
      kkPaint(card, card.kk, card.dataset.yourSize);
      return;
    }

    const button = event.target.closest('[data-kk-size]');
    if (!button || button.disabled || this.busy) return;
    const variant = kkVariantFor(card.kk, button.dataset.kkSize);
    if (!variant || !variant.available) return;

    this.busy = true;
    this.classList.add('is-busy');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    const error = card.querySelector('[data-kk-error]');
    if (error) error.hidden = true;

    kkAddVariant(variant.id, 'kk-cart-upsell').catch((e) => {
      this.busy = false;
      this.classList.remove('is-busy');
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      if (error) {
        error.textContent = (e && e.message) || window.cartStrings.error;
        error.hidden = false;
      }
    });
  }
}

if (!customElements.get('kk-cart-upsell')) customElements.define('kk-cart-upsell', KkCartUpsell);

/* ---------- Quick add on product cards ---------- */

// The touch version: one bottom sheet for the whole page, filled from whichever
// card's "+" was tapped. It lives on <body> because the cards transform on
// hover/focus, which would trap a fixed-position child inside the card.
const KkSheet = {
  el: null,
  card: null,

  build() {
    if (this.el) return;
    const el = document.createElement('div');
    el.className = 'kk-sheet';
    el.innerHTML = `
      <div class="kk-sheet__overlay" data-kk-sheet-close></div>
      <div class="kk-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="KkSheetTitle" tabindex="-1">
        <div class="kk-sheet__head">
          <img class="kk-sheet__img" alt="" width="72" height="90" data-kk-img>
          <div class="kk-sheet__meta">
            <p class="kk-sheet__title" id="KkSheetTitle"></p>
            <p class="kk-sheet__price"></p>
            <a class="kk-sheet__link" href="#">Full details</a>
          </div>
          <button type="button" class="kk-sheet__close" data-kk-sheet-close aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
              <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="2.6" stroke-linecap="square"/>
            </svg>
          </button>
        </div>
        <div class="kk-sheet__body"></div>
      </div>`;
    document.body.appendChild(el);
    this.el = el;
    this.panel = el.querySelector('.kk-sheet__panel');
    this.body = el.querySelector('.kk-sheet__body');

    el.addEventListener('click', (event) => {
      if (event.target.closest('[data-kk-sheet-close]')) {
        this.close();
        return;
      }
      if (this.card) this.card.choose(event, this.panel);
    });
    document.addEventListener('keyup', (event) => {
      if (event.code === 'Escape' && this.isOpen()) this.close();
    });
  },

  isOpen() {
    return !!this.el && this.el.classList.contains('is-open');
  },

  open(card) {
    this.build();
    this.card = card;
    const data = card.kk;
    this.el.querySelector('.kk-sheet__title').textContent = data.title;
    this.el.querySelector('.kk-sheet__price').textContent = data.priceLabel;
    this.el.querySelector('.kk-sheet__link').href = card.colourHref();
    const panel = card.querySelector('[data-kk-panel]');
    this.body.innerHTML = panel ? panel.innerHTML : '';
    kkPaint(this.panel, data, card.dataset.yourSize);
    // No hover on touch: fetch the other colours' card photos while the sheet is up.
    card.warmColours();

    kkLock();
    this.el.classList.add('is-open');
    setTimeout(() => {
      trapFocus(this.panel, this.panel.querySelector('[data-kk-size]:not(:disabled)') || this.panel);
    }, 60);
  },

  close() {
    if (!this.isOpen()) return;
    this.el.classList.remove('is-open');
    kkUnlock();
    // Back to the card's "+", unless the drawer is taking over after an add.
    const drawer = document.querySelector('cart-drawer');
    const drawerOpening = drawer && (drawer.holdsLock || drawer.classList.contains('active'));
    const opener = this.card && this.card.querySelector('.kk-qa__open');
    removeTrapFocus(opener && !drawerOpening ? opener : null);
    this.card = null;
  },
};

class KkQuickAdd extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.kk = kkReadVariants(this);
    if (!this.kk) {
      this.hidden = true;
      return;
    }
    kkPaint(this, this.kk, this.dataset.yourSize);
    this.startColour = this.kk.color;
    this.addEventListener('click', (event) => {
      if (event.target.closest('.kk-qa__open')) {
        KkSheet.open(this);
        return;
      }
      this.choose(event, this);
    });
    // Pointing at or tabbing to a swatch starts fetching that colour's photo, so it
    // is usually in hand by the click. Nothing extra loads with the page.
    const warm = (event) => {
      const swatch = event.target.closest && event.target.closest('[data-kk-color]');
      if (swatch && !swatch.disabled) this.warmColour(swatch.dataset.kkColor);
    };
    this.addEventListener('pointerover', warm);
    this.addEventListener('focusin', warm);
  }

  // root is this card's own panel (desktop) or the sheet (touch).
  choose(event, root) {
    const swatch = event.target.closest('[data-kk-color]');
    if (swatch && root.contains(swatch)) {
      if (swatch.disabled) return;
      this.kk.color = swatch.dataset.kkColor;
      kkPaint(this, this.kk, this.dataset.yourSize);
      if (root !== this) kkPaint(root, this.kk, this.dataset.yourSize);
      this.followColour();
      return;
    }

    const button = event.target.closest('[data-kk-size]');
    if (!button || !root.contains(button) || button.disabled || this.busy) return;
    const variant = kkVariantFor(this.kk, button.dataset.kkSize);
    if (!variant || !variant.available) return;

    this.busy = true;
    root.classList.add('is-busy');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    const error = root.querySelector('[data-kk-error]');
    if (error) error.hidden = true;

    const done = () => {
      this.busy = false;
      root.classList.remove('is-busy');
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    };

    kkAddVariant(variant.id, 'kk-quick-add')
      .then(() => {
        done();
        // After the drawer's own open timer, so the page lock passes straight to it.
        if (root !== this) setTimeout(() => KkSheet.close());
      })
      .catch((e) => {
        done();
        if (error) {
          error.textContent = (e && e.message) || window.cartStrings.error;
          error.hidden = false;
        }
      });
  }

  /* The card photo follows the picked colour. */

  // The card's photo box, its own photo and its link; null on a card without a photo.
  cardParts() {
    const card = this.closest('.kk-card');
    const media = card && card.querySelector('.kk-card__media');
    const primary = media && media.querySelector('.kk-card__image--primary');
    return primary ? { media, primary, link: card.querySelector('.kk-card__link') } : null;
  }

  // A colour's photo is the image of the variant shown for it (kk-variant-data), asked
  // for at the card photo's own widths and sizes, so it costs what the card photo does.
  colourPhoto(colour) {
    const parts = this.cardParts();
    const variant = kkShownVariant({ ...this.kk, color: colour });
    if (!parts || !variant || !variant.img) return null;
    const widths = (parts.primary.getAttribute('srcset') || '')
      .split(',')
      .map((candidate) => parseInt(candidate.trim().split(/\s+/)[1], 10))
      .filter((width) => width > 0);
    const srcset = (widths.length ? widths : [300, 450, 600, 800, 1000])
      .map((width) => {
        const url = new URL(variant.img, window.location.href);
        url.searchParams.set('width', width);
        return `${url.href} ${width}w`;
      })
      .join(', ');
    const path = (src) => new URL(src, window.location.href).pathname;
    return {
      srcset,
      sizes: parts.primary.getAttribute('sizes') || '100vw',
      // The card already shows this photo (the colour it opened with).
      onCard: path(variant.img) === path(parts.primary.getAttribute('src')),
    };
  }

  // Fetch and decode a colour's photo off-screen, once. Same srcset and sizes as the
  // layer gets, so the browser picks the same file and the layer takes it from memory.
  warmColour(colour) {
    this.warmed = this.warmed || new Map();
    if (!this.warmed.has(colour)) {
      const photo = this.colourPhoto(colour);
      let ready = Promise.resolve(false);
      if (photo && !photo.onCard) {
        const img = new Image();
        img.decoding = 'async';
        img.sizes = photo.sizes;
        img.srcset = photo.srcset;
        ready = img.decode().then(
          () => true,
          () => {
            this.warmed.delete(colour);
            return false;
          }
        );
      }
      this.warmed.set(colour, ready);
    }
    return this.warmed.get(colour);
  }

  warmColours() {
    if (this.kk.colorIndex < 0) return;
    new Set(this.kk.variants.map((v) => v.options[this.kk.colorIndex])).forEach((colour) => {
      if (colour !== this.kk.color) this.warmColour(colour);
    });
  }

  // The product page in the picked colour; the card's own link for the colour it opened with.
  colourHref() {
    if (this.baseHref === undefined) {
      const parts = this.cardParts();
      this.baseHref = (parts && parts.link && parts.link.getAttribute('href')) || this.kk.url;
    }
    const variant = this.kk.colorIndex >= 0 && this.kk.color !== this.startColour && kkShownVariant(this.kk);
    if (!variant) return this.baseHref;
    const url = new URL(this.baseHref, window.location.href);
    url.searchParams.set('variant', variant.id);
    return url.pathname + url.search + url.hash;
  }

  // Lays the picked colour's photo over the card's own and fades it in once decoded,
  // so there is never a blank frame; fades it out for the photo the card already shows.
  async followColour() {
    const parts = this.cardParts();
    const href = this.colourHref();
    if (parts && parts.link) parts.link.setAttribute('href', href);
    if (KkSheet.card === this) KkSheet.el.querySelector('.kk-sheet__link').href = href;

    const token = (this.photoToken = (this.photoToken || 0) + 1);
    const photo = this.colourPhoto(this.kk.color);
    if (!photo || photo.onCard) {
      if (this.layer) this.layer.classList.remove('is-shown');
      return;
    }
    if (!(await this.warmColour(this.kk.color)) || token !== this.photoToken) return;
    if (!this.layer) {
      this.layer = document.createElement('img');
      this.layer.className = 'kk-card__image kk-card__image--colour';
      this.layer.alt = '';
      this.layer.decoding = 'async';
      const images = parts.media.querySelectorAll('.kk-card__image');
      images[images.length - 1].after(this.layer);
    }
    this.layer.sizes = photo.sizes;
    this.layer.srcset = photo.srcset;
    try {
      await this.layer.decode();
    } catch (e) {
      return; // a newer pick replaced this one, or the file failed: the card keeps its photo
    }
    if (token === this.photoToken) this.layer.classList.add('is-shown');
  }
}

if (!customElements.get('kk-quick-add')) customElements.define('kk-quick-add', KkQuickAdd);

/* ---------- Sticky add to cart on product pages ---------- */

// The page's own picker and Add to cart stay the one source of truth: a chip here
// clicks the matching option up there, Add clicks the real button, and this bar
// re-reads the form's variant id whenever Dawn changes it.
class KkStickyAtc extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.kk = kkReadVariants(this);
    this.info = document.querySelector('product-info[id^="MainProduct-"]');
    this.mainForm = this.info && this.info.querySelector('product-form');
    if (!this.kk || !this.mainForm) {
      this.hidden = true;
      return;
    }
    this.addButton = this.querySelector('[data-kk-satc-add]');
    this.labelEl = this.querySelector('[data-kk-satc-label]');
    this.label = this.labelEl ? this.labelEl.textContent.trim() : 'Add to cart';
    this.error = this.querySelector('[data-kk-error]');

    // Straight under <body>, so no transformed ancestor can pin it inside the page.
    if (this.parentElement !== document.body && !(window.Shopify && Shopify.designMode)) {
      document.body.appendChild(this);
    }

    this.addEventListener('click', (event) => this.onClick(event));
    // Dawn rewrites the form's variant id on every switch, unavailable combinations included.
    this.info.addEventListener('change', (event) => {
      if (event.target.matches && event.target.matches('input[name="id"]')) {
        this.updating = false;
        this.sync();
        // Dawn swaps the photo right after this, in the same tick; keep the flag till then.
        setTimeout(() => {
          this.fromBar = false;
        });
      }
    });
    this.holdScrollForBarChoices();
    // When the new options are in, Dawn focuses the one that was clicked, which scrolls
    // the page up to the picker. For a choice made in this bar: page back where it was
    // before anything paints, and focus back on the bar's chip.
    this.info.addEventListener('focusin', (event) => {
      if (!this.fromBar || !event.target.closest || !event.target.closest('variant-selects')) return;
      // focus() scrolls after its focus events, so undo it once that call has returned
      // (still before the next paint).
      const y = this.holdY;
      const origin = this.origin;
      queueMicrotask(() => {
        kkJumpTo(y);
        if (origin && origin.isConnected) origin.focus({ preventScroll: true });
      });
    });
    subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, () => {
      this.updating = true;
      this.sync();
    });
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'product-form') this.done();
    });
    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      if (!this.busy) return;
      const errors = typeof event.errors === 'string' ? event.errors : '';
      this.done(errors || event.message || window.cartStrings.error);
    });

    this.sync();
    this.watch();
  }

  // Options picked on the page: from the variant when the combination exists,
  // else from the checked inputs (an unavailable combination has no variant).
  picked() {
    const idInput = this.mainForm.querySelector('input[name="id"]');
    const variant = idInput && this.kk.variants.find((v) => String(v.id) === idInput.value);
    if (variant) return { variant, values: variant.options.slice() };
    const values = [];
    const picker = this.info.querySelector('variant-selects');
    if (picker) {
      picker.querySelectorAll('input[type="radio"]:checked, select').forEach((el) => {
        const match = el.name.trim().match(/-(\d+)$/);
        if (match) values[Number(match[1]) - 1] = el.value;
      });
    }
    return { variant: null, values };
  }

  sync() {
    const data = this.kk;
    const { variant, values } = this.picked();
    if (data.colorIndex >= 0 && values[data.colorIndex]) data.color = values[data.colorIndex];
    const size = data.sizeIndex >= 0 ? values[data.sizeIndex] : null;

    kkPaint(this, data, '', 'select');
    this.querySelectorAll('[data-kk-size]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.kkSize === size ? 'true' : 'false');
    });
    const sizeName = this.querySelector('[data-kk-size-name]');
    if (sizeName && size) sizeName.textContent = size;

    if (variant) {
      this.querySelectorAll('[data-kk-price]').forEach((el) => {
        el.textContent = kkMoney(variant.price, true);
      });
      const img = this.querySelector('[data-kk-img]');
      if (img && variant.img) img.src = variant.img;
    }

    let label = this.label;
    if (!variant) label = (window.variantStrings && window.variantStrings.unavailable) || 'Unavailable';
    else if (!variant.available) label = (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
    if (this.labelEl) this.labelEl.textContent = label;
    this.addButton.disabled = !(variant && variant.available) || this.updating || this.busy;
  }

  // Click the page's own option, so Dawn updates price, photos, URL and the form.
  pick(position, value, origin) {
    const picker = this.info.querySelector('variant-selects');
    if (!picker) return;
    const input = [...picker.querySelectorAll('input[type="radio"]')].find(
      (el) => el.name.trim().endsWith(`-${position}`) && el.value === value
    );
    const select = input
      ? null
      : [...picker.querySelectorAll('select')].find((el) => el.name.trim().endsWith(`-${position}`));
    if (input ? input.checked : !select || select.value === value) return;

    this.fromBar = true;
    this.holdY = window.scrollY;
    this.origin = origin;
    clearTimeout(this.fromBarTimer);
    this.fromBarTimer = setTimeout(() => {
      this.fromBar = false;
    }, 4000);
    if (input) {
      input.click();
    } else {
      select.value = value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // On every variant switch Dawn's gallery scrolls the window up to the new photo.
  // Right for the picker beside the photos; for a choice made down here in the bar it
  // would yank the shopper to the top (and the bar away), so for those the window
  // stays put. The photo itself still switches.
  holdScrollForBarChoices() {
    const gallery = this.info.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function' || gallery.kkHeld) return;
    const setActiveMedia = gallery.setActiveMedia.bind(gallery);
    gallery.setActiveMedia = (mediaId, prepend) => {
      if (!this.fromBar) return setActiveMedia(mediaId, prepend);
      const scrollTo = window.scrollTo;
      window.scrollTo = () => {};
      setActiveMedia(mediaId, prepend);
      // Dawn scrolls in a zero-delay timeout queued just now; put scrollTo back after it.
      setTimeout(() => {
        window.scrollTo = scrollTo;
      });
    };
    gallery.kkHeld = true;
  }

  onClick(event) {
    const swatch = event.target.closest('[data-kk-color]');
    if (swatch) {
      if (!swatch.disabled && !this.busy) this.pick(this.kk.colorIndex + 1, swatch.dataset.kkColor, swatch);
      return;
    }
    const size = event.target.closest('[data-kk-size]');
    if (size) {
      if (!size.disabled && !this.busy) this.pick(this.kk.sizeIndex + 1, size.dataset.kkSize, size);
      return;
    }
    if (event.target.closest('[data-kk-satc-add]')) this.add();
  }

  add() {
    if (this.busy || this.updating) return;
    const submit = this.mainForm.querySelector('button[type="submit"][name="add"]');
    if (!submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
    this.busy = true;
    this.addButton.disabled = true;
    this.addButton.classList.add('is-loading');
    if (this.error) this.error.hidden = true;
    submit.click();
    clearTimeout(this.busyTimer);
    this.busyTimer = setTimeout(() => this.done(), 10000);
  }

  done(message) {
    clearTimeout(this.busyTimer);
    if (!this.busy) return;
    this.busy = false;
    this.addButton.classList.remove('is-loading');
    if (message && this.error) {
      this.error.textContent = message;
      this.error.hidden = false;
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => {
        this.error.hidden = true;
      }, 6000);
    }
    this.sync();
  }

  // In view only while the page's Add to cart + Buy it now block is above the screen.
  // Checked per frame on scroll rather than with an IntersectionObserver: a jump straight
  // from the bottom to the top (an iPhone status-bar tap) never "intersects" the block,
  // so an observer would leave the bar up at the top of the page.
  watch() {
    let queued = false;
    const update = () => {
      queued = false;
      const on = this.mainForm.getBoundingClientRect().bottom < 0;
      if (on === this.isOn) return;
      this.isOn = on;
      this.classList.toggle('is-visible', on);
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
      this.inert = !on;
      // Room under the footer, so the bar never sits on its last lines.
      const shown = on && this.getClientRects().length > 0;
      if (shown) document.body.style.setProperty('--kk-satc-h', `${this.offsetHeight}px`);
      document.body.classList.toggle('kk-satc-on', shown);
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    update();
  }
}

if (!customElements.get('kk-sticky-atc')) customElements.define('kk-sticky-atc', KkStickyAtc);
